import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer.jsx';
import { Link } from 'react-router-dom';
import Header from '../components/Header.jsx';
import { adminAPI } from '../services/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalFeedback: 0,
    pendingFeedback: 0,
    totalProducts: 0,
    totalEvents: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await adminAPI.getStats();
      if (response.data && response.data.stats) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      // Keep default stats values on error
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Users', value: stats?.totalUsers || 0, icon: '👥', color: '#7a132b', link: '/admin/users' },
    { title: 'Active Users', value: stats?.activeUsers || 0, icon: '✅', color: '#059669', link: '/admin/users' },
    { title: 'Total Feedback', value: stats?.totalFeedback || 0, icon: '⭐', color: '#ffc107', link: '/admin/feedback' },
    { title: 'Pending Reviews', value: stats?.pendingFeedback || 0, icon: '⏳', color: '#f97316', link: '/admin/feedback' },
    { title: 'Products', value: stats?.totalProducts || 0, icon: '🛍️', color: '#8b5cf6', link: '/admin/products' },
    { title: 'Events', value: stats?.totalEvents || 0, icon: '📅', color: '#06b6d4', link: '/admin/events' }
  ];

  return (
    <div>
      <Header />
      <main>
        <section className="section">
          <div style={{ marginBottom: '30px' }}>
            <h1 style={{ 
              color: 'var(--maroon)', 
              fontFamily: "'Playfair Display',serif", 
              fontSize: '36px', 
              marginBottom: '8px' 
            }}>
              Admin Dashboard
            </h1>
            <p style={{ color: 'var(--muted)' }}>Manage your website content and users</p>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px', color: 'var(--muted)' }}>
              Loading statistics...
            </div>
          ) : (
            <>
              {/* Statistics Cards */}
              <div className="admin-stats-grid">
                {statCards.map((stat, index) => (
                  <Link to={stat.link} key={index} style={{ textDecoration: 'none' }}>
                    <div className="card" style={{
                      background: `linear-gradient(135deg, ${stat.color}15, ${stat.color}05)`,
                      border: `2px solid ${stat.color}30`,
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      ':hover': { transform: 'translateY(-4px)' }
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <p style={{ 
                            color: 'var(--muted)', 
                            fontSize: '14px', 
                            margin: '0 0 8px',
                            fontWeight: '600'
                          }}>
                            {stat.title}
                          </p>
                          <h2 style={{ 
                            color: stat.color, 
                            fontSize: '32px', 
                            margin: 0,
                            fontWeight: '800'
                          }}>
                            {stat.value}
                          </h2>
                        </div>
                        <div style={{ fontSize: '48px', opacity: 0.6 }}>
                          {stat.icon}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="card" style={{ padding: '32px', marginBottom: '30px' }}>
                <h2 style={{ 
                  color: 'var(--maroon)', 
                  fontFamily: "'Playfair Display',serif", 
                  marginBottom: '20px' 
                }}>
                  Quick Actions
                </h2>
                <div className="admin-quick-actions">
                  <Link to="/admin/users">
                    <button className="book" style={{ width: '100%', padding: '12px' }}>
                      👥 Manage Users
                    </button>
                  </Link>
                  <Link to="/admin/products">
                    <button className="book" style={{ width: '100%', padding: '12px' }}>
                      ➕ Add Product
                    </button>
                  </Link>
                  <Link to="/admin/events">
                    <button className="book" style={{ width: '100%', padding: '12px' }}>
                      📅 Create Event
                    </button>
                  </Link>
                  <Link to="/admin/feedback">
                    <button className="book" style={{ width: '100%', padding: '12px' }}>
                      ⭐ Review Feedback
                    </button>
                  </Link>
                  <Link to="/admin/image-library">
                    <button className="book" style={{ width: '100%', padding: '12px' }}>
                      🖼️ Image Library
                    </button>
                  </Link>
                  <Link to="/admin/gallery">
                    <button className="book" style={{ width: '100%', padding: '12px' }}>
                      📸 Gallery
                    </button>
                  </Link>
                  <Link to="/admin/certificates">
                    <button className="book" style={{ width: '100%', padding: '12px' }}>
                      📜 Certificates
                    </button>
                  </Link>
                </div>
              </div>

              {/* Admin Menu */}
              <div className="admin-menu-grid">
                <Link to="/admin/users" style={{ textDecoration: 'none' }}>
                  <div className="card" style={{ cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                    <h3 style={{ color: 'var(--maroon)', marginBottom: '8px' }}>User Management</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
                      View, search, activate or deactivate users
                    </p>
                  </div>
                </Link>

                <Link to="/admin/feedback" style={{ textDecoration: 'none' }}>
                  <div className="card" style={{ cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⭐</div>
                    <h3 style={{ color: 'var(--maroon)', marginBottom: '8px' }}>Feedback Management</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
                      Approve, reject or delete customer reviews
                    </p>
                  </div>
                </Link>

                <Link to="/admin/products" style={{ textDecoration: 'none' }}>
                  <div className="card" style={{ cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🛍️</div>
                    <h3 style={{ color: 'var(--maroon)', marginBottom: '8px' }}>Product Management</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
                      Add, edit or remove shop products
                    </p>
                  </div>
                </Link>

                <Link to="/admin/events" style={{ textDecoration: 'none' }}>
                  <div className="card" style={{ cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
                    <h3 style={{ color: 'var(--maroon)', marginBottom: '8px' }}>Event Management</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
                      Create, update or cancel events
                    </p>
                  </div>
                </Link>

                <Link to="/admin/image-library" style={{ textDecoration: 'none' }}>
                  <div className="card" style={{ cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🖼️</div>
                    <h3 style={{ color: 'var(--maroon)', marginBottom: '8px' }}>Image Library</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
                      Upload and manage reusable images
                    </p>
                  </div>
                </Link>

                <Link to="/admin/gallery" style={{ textDecoration: 'none' }}>
                  <div className="card" style={{ cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📸</div>
                    <h3 style={{ color: 'var(--maroon)', marginBottom: '8px' }}>Gallery</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
                      Manage public gallery images
                    </p>
                  </div>
                </Link>

                <Link to="/admin/certificates" style={{ textDecoration: 'none' }}>
                  <div className="card" style={{ cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📜</div>
                    <h3 style={{ color: 'var(--maroon)', marginBottom: '8px' }}>Certificate Management</h3>
                    <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
                      Add, edit or verify certificates
                    </p>
                  </div>
                </Link>
              </div>
            </>
          )}
        </section>

        {/* <footer style={{ marginTop: '50px' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto', textAlign: 'center', color: 'var(--muted)' }}>
            © {new Date().getFullYear()} Shree Rashi Jyotish - Admin Panel
          </div>
        </footer> */}

        <Footer />

      </main>
    </div>
  );
};

export default AdminDashboard;
